/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import gatewayFetch from "utilities/gatewayFetch";
import { mapToServerFormat } from "utilities/ApiUtilities";
import FilterTypes from "constants/FilterTypes";
import { CurrentUser } from "reducers/CurrentUserReducer";
import {
  redactNumericPathSegments,
  redactUuids,
} from "utilities/StringUtilities";
import {
  EnhanceSpeechTrackErrorType,
  LimitReachedEnhanceSpeechTrackErrorType,
} from "components/EnhancedSpeech/EnhanceSpeechTrack";

export enum AnalyticsEvent {
  USER_EXPORTS_PROJECT = "user_exports_project",
  USER_EXPORTS_PROJECT_CLIP = "user_exports_project_clip",
  USER_EXPORTS_PROJECT_TRANSCRIPT = "user_exports_project_transcript",
  USER_MIC_CHECK = "user_mic_check",
  SESSION_LOAD = "session_load",
  USER_OPENS_MUSIC_COLLECTION_PANEL = "user_opens_music_collection_panel",
  USER_ADDS_MUSIC_COLLECTION_ITEM = "user_adds_music_collection_item",
  START_RECORDING_ELAPSED_TIME = "start_recording_elapsed_time",
  USER_CLICKS_UPGRADE_BUTTON = "user_clicks_upgrade_button",
  ENHANCE_UPLOAD_FAILED = "enhance_upload_failed",
  USER_VISIT_FREE_ENHANCE = "user_visited_free_enhance",
  USER_VISIT_PREMIUM_ENHANCE = "user_visited_premium_enhance",
  USER_VISIT_MIC_CHECK = "user_visited_miccheck",
  USER_VISIT_FEATURES = "user_visited_features",
  EXPRESS_PREMIUM_CHECKOUT_STARTED = "express_premium_checkout_started",
  EXPRESS_PREMIUM_CHECKOUT_COMPLETED = "express_premium_checkout_completed",
  EXPRESS_PREMIUM_CHECKOUT_CANCELLED = "express_premium_checkout_cancelled",
  USER_TRIGGERS_VIDEO_UPSELL = "user_triggers_video_upsell",
  FREE_USER_DOWNLOAD_PROJECT_LENGTH_EXCEEDED = "free_user_download_project_length_exceeded",
  FREE_USER_DAILY_DOWNLOAD_PROJECTS_LIMIT = "free_user_daily_download_projects_limit",
  FREE_USER_TRIGGERS_DOWNLOAD_ORIGINAL_TRACKS_PAYWALL = "free_user_triggers_download_original_tracks_paywall",
  USER_INVITED_BY_EMAIL = "user_invited_by_email",
  USER_CREATED_INVITE_LINK = "user_created_invite_link",
  USER_REVOKED_INVITE_LINK = "user_revoked_invite_link",
  USER_JOINED_PUBLIC_SESSION = "user_joined_public_session",
  BROWSER_ENFORCEMENT_PAGE_VIEWED = "browser_enforcement_page_viewed",
  USE_CASE_STARTED_RECORDING = "use_case_started_recording",
  USE_CASE_TRANSCRIBED_AUDIO = "use_case_transcribed_audio",
  USE_CASE_EDITED_AUDIO = "use_case_edited_audio",
  USER_UPLOADED_MY_MUSIC = "user_uploaded_my_music",
  USER_CLICKED_ENHANCE_STUDIO_NUDGE = "user_clicked_enhance_studio_nudge",
  USER_EXPORTS_AUDIOGRAM = "user_exports_audiogram",
  USER_EXPORTS_AUDIOGRAM_CLIP = "user_exports_audiogram_clip",
  USER_EXPORTS_VIDEO = "user_exports_video",
  JTBD_ENHANCE_HOME = "jtbd_enhance_home",
  JTBD_RECORD_HOME = "jtbd_record_home",
  JTBD_TRANSCRIBE_HOME = "jtbd_transcribe_home",
  JTBD_EDIT_HOME = "jtbd_edit_home",
  JTBD_MICCHECK_HOME = "jtbd_miccheck_home",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_1 = "home_template_open_project_clicked_1",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_2 = "home_template_open_project_clicked_2",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_3 = "home_template_open_project_clicked_3",
}

// Don't add new position param values without consulting
// https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=dva&title=DVA+SDM+Analytics+Lexicon#MainCards--618193127
export enum ExpressCheckoutButtonPosition {
  FREE_ENHANCED_SPEECH_BENTO_BOX = "click:free-enhanced-speech-bento-box",
  FEATURES_BENTO_BOX = "click:features-bento-box",
  FEATURES_PREMIUM_PANEL = "click:features-premium-panel-button",
  FEATURES_FOOTER = "click:features-footer-button",
  FEATURES_HEADER = "click:features-header-button",
  FEATURES_PROFILE_DROPDOWN = "click:features-profile-dropdown-button",
  FREE_ENHANCE_UPGRADE_ROW = "click:free-enhance-upgrade-row",
  UPGRADE_BUTTON_POPUP_HEADER = "click:upgrade-button-popup-header",
  UPGRADE_BUTTON_POPUP_PROFILE_DROPDOWN = "click:upgrade-button-popup-profile-dropdown",
  ENHANCE_PROCESSING_UPSELL = "click:free-enhance-processing-upsell",
  ENHANCE_FINISHED_UPSELL = "click:free-enhance-finished-upsell",
  STUDIO_DOWNLOAD_ORIGINAL_TRACKS_UPSELL = "click:free-studio-download-original-tracks-upsell",
  STUDIO_DOWNLOAD_SIZE_LIMIT_UPSELL = "click:free-studio-download-size-limit-upsell",
  STUDIO_DOWNLOAD_FREQUENCY_LIMIT_UPSELL = "click:free-studio-download-frequency-limit-upsell",
  STUDIO_DOWNLOAD_PANEL = "click:free-studio-download-panel-upsell",
  STUDIO_STRENGTH_SLIDER_UPSELL = "click:free-studio-strength-slider-upsell",
  STUDIO_UPGRADE_MODAL = "click:free-studio-upgrade-modal",
  ENHANCE_LIMIT_FILE_SIZE_LIMIT = "click:free-enhance-limit:file_size_limit",
  ENHANCE_LIMIT_FILE_DURATION_LIMIT = "click:free-enhance-limit:file_duration_limit",
  ENHANCE_LIMIT_CONCURRENCY_LIMIT = "click:free-enhance-limit:concurrency_limit",
  ENHANCE_LIMIT_CUMULATIVE_DURATION_LIMIT = "click:free-enhance-limit:cumulative_duration_limit",
  ENHANCE_LIMIT_VIDEO_NOT_ACCEPTED = "click:free-enhance-limit:video_not_accepted",
  ENHANCE_LIMIT_MULTI_FILE_LIMIT = "click:free-enhance-limit:multi_file_limit",
  PAGE_LOAD = "page_load",
  AUDIOGRAM_REMOVE_BRANDING = "audiogram_remove_branding",
  AUDIOGRAM_THEME_UPGRADE = "audiogram_theme_upgrade",
  VIDEO_RESOLUTION_UPGRADE = "video_resolution_upgrade",
  VIDEO_REMOVE_BRANDING = "video_remove_branding",
}

export enum PostEnhanceStudioNudgeButtonPosition {
  GET_TRANSCRIPT_STUDIO_NUDGE = "click:get-transcript-studio-nudge",
  MAKE_EDITS_STUDIO_NUDGE = "click:make-edits-studio-nudge",
  ADD_MUSIC_STUDIO_NUDGE = "click:add-music-studio-nudge",
}

export const LimitReachedErrorToCheckoutPosition: {
  [T in LimitReachedEnhanceSpeechTrackErrorType]: ExpressCheckoutButtonPosition;
} = {
  [EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_CONCURRENCY_LIMIT,
  [EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_CUMULATIVE_DURATION_LIMIT,
  [EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_FILE_DURATION_LIMIT,
  [EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_FILE_SIZE_LIMIT,
  [EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_VIDEO_NOT_ACCEPTED,
  [EnhanceSpeechTrackErrorType.MULTI_FILE_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_MULTI_FILE_LIMIT,
};

export enum NewRelicEvent {
  CLIP_RECOVERY_UPLOAD_STATE_UPDATED = "CLIP_RECOVERY_UPLOAD_STATE_UPDATED",
  PARTICIPANT_RECORDING_SESSION_UPLOAD_FAILED = "PARTICIPANT_RECORDING_SESSION_UPLOAD_FAILED",
  RECORDING_UPLOADED = "RECORDING_UPLOADED",
  SELF_INITIATED_STOP_RECORDING = "SELF_INITIATED_STOP_RECORDING",
  ABANDON_RECORDING = "ABANDON_RECORDING",
  RECORDING_BLOCKED_BY_MIC_PERMISSIONS = "RECORDING_BLOCKED_BY_MIC_PERMISSIONS",
  BROWSER_ENFORCEMENT_PAGE_VIEWED = "BROWSER_ENFORCEMENT_PAGE_VIEWED",
  RECORDING_SESSION_ALREADY_FINALIZED = "RECORDING_SESSION_ALREADY_FINALIZED",
  RECORDING_SESSION_IMPORT_CREATED = "RECORDING_SESSION_IMPORT_CREATED",
  RECORDING_SESSION_IMPORT_FAILED = "RECORDING_SESSION_IMPORT_FAILED",
}

export function generateProjectMetadata(project: any, duration: Number): any {
  const metadata: any = {
    projectId: project.id,
    duration,
  };

  for (const key of Object.values(FilterTypes)) {
    const value = project.filters && project.filters[key]?.enabled;
    metadata[key] = value || false;
  }

  return metadata;
}

export interface StoredEvent {
  event: AnalyticsEvent;
  params: Record<string, any>;
}

interface StoredEventHandler {
  handleEvent: (event: StoredEvent) => void;
  processEventQueue: (queue: StoredEvent[]) => StoredEvent[];
}

class AnalyticsLogger {
  private events: StoredEvent[] = [];
  private eventHandler: StoredEventHandler | undefined;
  private currentUser: CurrentUser | null = null;

  public setEventHandler(handler: StoredEventHandler): void {
    this.eventHandler = handler;
    this.handleEvents();
  }

  public setCurrentUser(currentUser: CurrentUser | null): void {
    this.currentUser = currentUser;
  }

  public logEvent(
    event: AnalyticsEvent,
    additionalParams: Record<string, any> = {}
  ): void {
    if (!this.currentUser) {
      console.warn(
        `Attempted to send analytics event ${event} for logged out user.`
      );
      return;
    }

    const location = redactNumericPathSegments(
      redactUuids(window.location.pathname)
    );

    this.events.push({
      event: event,
      params: {
        ...additionalParams,
        location: location,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        has_premium_access: this.currentUser?.hasPremiumAccess,
        user_is_logged_in: !this.currentUser?.isAnonymous,
      },
    });
    this.handleEvents();
  }

  private handleEvents(): void {
    if (this.eventHandler === undefined) {
      return;
    }
    for (const event of this.events) {
      this.eventHandler.handleEvent(event);
    }
    this.events = this.eventHandler.processEventQueue(this.events);
  }
}

export namespace Analytics {
  export const instance = new AnalyticsLogger();

  export const nullHandler: StoredEventHandler = {
    handleEvent: (): void => {},
    processEventQueue: (queue) => queue,
  };

  export const loggingHandler: StoredEventHandler = {
    handleEvent: (event): void => {
      gatewayFetch(`/api/v1/analytics_event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mapToServerFormat({
            eventName: event.event,
            additionalParams: event.params,
          })
        ),
      }).then(() => {});
    },
    processEventQueue: (queue) => [],
  };
}

export default Analytics;
